import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './App.css';
import { AppHolder, ThemedGlobalStyle } from './settings';
import { AuthProvider, useAuth } from './hooks/useAuth';

import { Login } from './modules/AuthPages';
import { Dashboard } from './modules/Dashboard';
import LandingContainer from './modules/landingPages/landingContainer';
import RootLayout from './components/layout/RootLayout';
import { darkTheme } from './settings/appStyle/theme';
import { UploadDocuments } from './modules/UploadDocuments';
import { Processing } from './modules/Processing';

const queryClient = new QueryClient();

// ============================================
// Protected Route
// ============================================
const ProtectedRoute = () => {
  const { session } = useAuth();
  const location = useLocation();

  const isLoggedIn = Boolean(session?.token);

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

// ============================================
// Public Route
// ============================================
const PublicRoute = () => {
  const { session } = useAuth();
  const isLoggedIn = Boolean(session?.token);

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

// ============================================
// Protected User Routes
// ============================================
const UserRoutes = () => {
  return (
    <Routes>
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<RootLayout />}>
          {/* "/" */}
          <Route index element={<Dashboard />} />
          <Route path="uploadDocuments" element={<UploadDocuments />} />
          <Route path="processing/:id" element={<Processing />} />
        </Route>
      </Route>
    </Routes>
  );
};

// ============================================
// Dynamic root handler
// ============================================
const UserOrPublicRoot = () => {
  const { session } = useAuth();
  const isLoggedIn = Boolean(session?.token);

  return (
    <AppHolder id="app-global">{isLoggedIn ? <UserRoutes /> : <LandingContainer />}</AppHolder>
  );
};

// ============================================
// App Router
// ============================================
const AppRouter = () => {
  return (
    <Routes>
      {/* login */}
      <Route element={<PublicRoute />}>
        <Route
          path="/login"
          element={
            <AppHolder id="app-global">
              <Login />
            </AppHolder>
          }
        />
      </Route>

      {/* public landing pages */}
      <Route
        path="/policy"
        element={
          <AppHolder id="app-global">
            <LandingContainer />
          </AppHolder>
        }
      />

      <Route
        path="/terms"
        element={
          <AppHolder id="app-global">
            <LandingContainer />
          </AppHolder>
        }
      />

      {/* "/" and protected pages */}
      <Route path="/*" element={<UserOrPublicRoot />} />

      {/* fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// ============================================
// Theme wrapper
// ============================================
const SecuredApp = () => {
  return (
    <ThemeProvider theme={darkTheme}>
      <ThemedGlobalStyle />
      <QueryClientProvider client={queryClient}>
        <AppRouter />
      </QueryClientProvider>
    </ThemeProvider>
  );
};

// ============================================
// App
// ============================================
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SecuredApp />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
